import React, { useState, useRef, useEffect } from 'react';
import { X, Save, Play, Pause, Mail, Clock, GitBranch, Plus, Trash2, Layers, MessageCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import FullScreenModal from '../../../components/Modal/FullScreenModal';
import DeleteConfirmationModal from '@/components/Modal/DeleteConfirmationModal';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  useDraggable,
  useDroppable,
  DragStartEvent,
  DragEndEvent
} from '@dnd-kit/core';
import {
  getTemplates,
  getIntegrationTemplates,
  createCampaign,
  updateCampaign,
  pauseCampaign,
  activateCampaign,
  DripEmailTemplate,
  IntegrationTemplate,
  AutoEnrollmentConfig
} from '@/actions/drip-campaigns';
import EnrollmentSettings from './EnrollmentSettings';

interface CampaignStep {
  id: string;
  type: 'email' | 'whatsapp' | 'delay' | 'conditional';
  order: number;
  position: { x: number; y: number };
  data: any;
  nextStepId?: string; // For regular flow
  yesStepId?: string; // For conditional YES branch
  noStepId?: string; // For conditional NO branch
}

interface Campaign {
  id?: number;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused';
  type: 'drip' | 'status_change';
  steps: CampaignStep[];
  auto_enrollment_config?: AutoEnrollmentConfig | null;
}

interface CampaignBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign?: any; // Existing campaign from API
  onSuccess: () => void;
}

const CampaignBuilderModal: React.FC<CampaignBuilderModalProps> = ({
  isOpen,
  onClose,
  campaign: initialCampaign,
  onSuccess
}) => {
  const [campaign, setCampaign] = useState<Campaign>({
    name: 'New Campaign',
    description: '',
    status: 'draft',
    type: 'drip',
    steps: [],
    auto_enrollment_config: {
      enabled: true,
      trigger_type: 'new_lead',
      conditions: [],
      logic: 'AND'
    }
  });
  const [activeTab, setActiveTab] = useState<'workflow' | 'enrollment'>('enrollment');

  const [selectedStep, setSelectedStep] = useState<CampaignStep | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dragType, setDragType] = useState<'new' | 'existing' | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState<DripEmailTemplate[]>([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState<IntegrationTemplate[]>([]);
  const [showDeleteStepModal, setShowDeleteStepModal] = useState(false);
  const [stepToDelete, setStepToDelete] = useState<CampaignStep | null>(null);
  const [hasWhatsappIntegration, setHasWhatsappIntegration] = useState<boolean | null>(null);
  const [hasEmailTemplates, setHasEmailTemplates] = useState<boolean | null>(null);
  const [whatsappProvider, setWhatsappProvider] = useState<'wati' | 'aisensy' | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Load templates and campaign data
  useEffect(() => {
    if (isOpen) {
      loadTemplates();

      if (initialCampaign) {
        // Transform API campaign to visual builder format
        console.log('Loading campaign for edit:', initialCampaign);
        const transformedCampaign = transformApiToVisual(initialCampaign);
        console.log('Transformed campaign:', transformedCampaign);
        console.log('Campaign steps count:', transformedCampaign.steps.length);
        setCampaign(transformedCampaign);
      } else {
        // Reset for new campaign - auto-enrollment enabled by default
        setCampaign({
          name: 'New Campaign',
          description: '',
          status: 'draft',
          type: 'drip',
          steps: [],
          auto_enrollment_config: {
            enabled: true,
            trigger_type: 'new_lead',
            conditions: [],
            logic: 'AND'
          }
        });
      }
      setSelectedStep(null);
    }
  }, [isOpen, initialCampaign]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);

      // Fetch email templates
      try {
        const emailResponse = await getTemplates();
        if (emailResponse.success && emailResponse.data) {
          setTemplates(emailResponse.data);
          setHasEmailTemplates(emailResponse.data.length > 0);
        } else {
          setHasEmailTemplates(false);
        }
      } catch (error) {
        console.error('Error loading email templates:', error);
        setHasEmailTemplates(false);
      }

      // Fetch WhatsApp templates separately to handle integration not found gracefully
      try {
        const whatsappResponse = await getIntegrationTemplates({ channel: 'whatsapp', status: 'APPROVED' });
        if (whatsappResponse.success && whatsappResponse.data) {
          setWhatsappTemplates(whatsappResponse.data);
          setHasWhatsappIntegration(whatsappResponse.data.length > 0);
          if (whatsappResponse.provider) {
            setWhatsappProvider(whatsappResponse.provider as 'wati' | 'aisensy');
          }
        } else {
          setHasWhatsappIntegration(false);
        }
      } catch (error) {
        // Integration not found - this is expected if WhatsApp isn't configured
        console.log('WhatsApp integration not configured:', error);
        setHasWhatsappIntegration(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Transform API campaign to visual builder format
  const transformApiToVisual = (apiCampaign: any): Campaign => {
    const visualSteps: CampaignStep[] = [];

    console.log('transformApiToVisual - API steps:', apiCampaign.steps);

    if (apiCampaign.steps && apiCampaign.steps.length > 0) {
      // Convert API steps to visual steps with auto-layout
      apiCampaign.steps.forEach((step: any, index: number) => {
        // Determine step type
        let stepType: 'email' | 'whatsapp' | 'conditional';
        if (step.is_conditional) {
          stepType = 'conditional';
        } else if (step.integration_template_id_fk) {
          stepType = 'whatsapp';
        } else {
          stepType = 'email';
        }

        const visualStep: CampaignStep = {
          id: `step-${step.id}`,
          type: stepType,
          order: step.step_order,
          position: {
            x: 100,
            y: 100 + (index * 200) // Vertical spacing
          },
          data: {
            stepId: step.id,
            templateId: step.template_id_fk || step.integration_template_id_fk,
            stepName: step.step_name,
            subject: stepType === 'email' ? (step.template?.subject_line || '') : undefined,
            trackOpens: stepType === 'email' ? true : undefined,
            parameterMapping: step.parameter_mapping || {},
            // Conditional step data
            conditionType: stepType === 'conditional' ? step.condition_type : undefined,
            waitHours: stepType === 'conditional' ? step.condition_wait_hours : undefined
          }
        };

        // Map conditional branch connections
        if (stepType === 'conditional') {
          if (step.yes_branch_step_id) {
            visualStep.yesStepId = `step-${step.yes_branch_step_id}`;
          }
          if (step.no_branch_step_id) {
            visualStep.noStepId = `step-${step.no_branch_step_id}`;
          }
        }

        // Add delay step if delay > 0
        if (step.delay_value > 0 && index < apiCampaign.steps.length - 1) {
          const delayStep: CampaignStep = {
            id: `delay-${step.id}`,
            type: 'delay',
            order: step.step_order + 0.5,
            position: {
              x: 100,
              y: 100 + (index * 200) + 100
            },
            data: {
              delayValue: step.delay_value,
              delayUnit: step.delay_unit
            }
          };

          visualStep.nextStepId = delayStep.id;
          visualSteps.push(visualStep, delayStep);

          // Connect delay to next email step
          if (index < apiCampaign.steps.length - 1) {
            delayStep.nextStepId = `step-${apiCampaign.steps[index + 1].id}`;
          }
        } else {
          visualSteps.push(visualStep);

          // Connect to next step (if not conditional, as conditional uses yes/no paths)
          if (stepType !== 'conditional' && index < apiCampaign.steps.length - 1) {
            visualStep.nextStepId = `step-${apiCampaign.steps[index + 1].id}`;
          }
        }
      });
    }

    console.log('transformApiToVisual - Transformed visual steps:', visualSteps);

    return {
      id: apiCampaign.id,
      name: apiCampaign.campaign_name,
      description: apiCampaign.description || '',
      status: apiCampaign.status,
      type: apiCampaign.enrollment_type === 'auto_trigger' ? 'status_change' : 'drip',
      steps: visualSteps,
      auto_enrollment_config: apiCampaign.auto_enrollment_config || null
    };
  };

  // Transform visual builder format to API format
  const transformVisualToApi = () => {
    // Validate campaign
    if (!campaign.name.trim()) {
      throw new Error('Please enter a campaign name');
    }

    if (campaign.steps.length === 0) {
      throw new Error('Please add at least one step to your campaign');
    }

    // Get all actionable steps (email, WhatsApp, conditional) - exclude delay as it's merged with steps
    const actionableSteps = campaign.steps
      .filter(step => step.type === 'email' || step.type === 'whatsapp' || step.type === 'conditional')
      .sort((a, b) => a.order - b.order);

    // Create a mapping of visual step IDs to their step_order
    const stepIdToOrder = new Map<string, number>();
    actionableSteps.forEach((step, index) => {
      stepIdToOrder.set(step.id, index + 1);
    });

    // Build API steps
    const apiSteps = actionableSteps.map((step, index) => {
      // Find delay step after this step
      const delayStep = campaign.steps.find(
        s => s.type === 'delay' && step.nextStepId === s.id
      );

      const apiStep: any = {
        step_name: step.data?.stepName || `Step ${index + 1}`,
        step_order: index + 1,
        delay_value: delayStep?.data?.delayValue || 0,
        delay_unit: delayStep?.data?.delayUnit || 'days',
        delay_from: index === 0 ? 'enrollment' : 'previous_step',
        parameter_mapping: step.data?.parameterMapping || {}
      };

      // Handle different step types
      if (step.type === 'email') {
        apiStep.template_id_fk = step.data?.templateId || 0;
        apiStep.integration_template_id_fk = null;
        apiStep.is_conditional = false;
      } else if (step.type === 'whatsapp') {
        apiStep.template_id_fk = null;
        apiStep.integration_template_id_fk = step.data?.templateId || 0;
        apiStep.is_conditional = false;
      } else if (step.type === 'conditional') {
        // Conditional step
        apiStep.template_id_fk = null;
        apiStep.integration_template_id_fk = null;
        apiStep.is_conditional = true;
        apiStep.condition_type = step.data?.conditionType || 'email_opened';
        apiStep.condition_wait_hours = step.data?.waitHours || 24;

        // Map yes/no branch step IDs to step_order
        // Store as metadata that backend will use to link steps after creation
        if (step.yesStepId) {
          const yesStepOrder = stepIdToOrder.get(step.yesStepId);
          apiStep._yes_step_order = yesStepOrder || null;
        }
        if (step.noStepId) {
          const noStepOrder = stepIdToOrder.get(step.noStepId);
          apiStep._no_step_order = noStepOrder || null;
        }
      }

      return apiStep;
    });

    // Validate all message steps have templates (conditional steps don't need templates)
    const invalidSteps = apiSteps.filter(step =>
      !step.is_conditional && !step.template_id_fk && !step.integration_template_id_fk
    );
    if (invalidSteps.length > 0) {
      throw new Error('All email and WhatsApp steps must have a template selected');
    }

    // Determine enrollment type based on auto-enrollment config
    const enrollmentType = campaign.auto_enrollment_config?.enabled
      ? 'auto_trigger'
      : 'manual';

    // Build auto_enrollment_trigger from auto_enrollment_config for backend validation
    const autoEnrollmentTrigger = campaign.auto_enrollment_config?.enabled
      ? {
          trigger_type: campaign.auto_enrollment_config.trigger_type || 'new_lead',
          trigger_config: campaign.auto_enrollment_config.trigger_type === 'status_change'
            ? {
                to_value: campaign.auto_enrollment_config.conditions?.find(c => c.field === 'status')?.value || null,
                template_id: null // Will be set via steps
              }
            : null
        }
      : null;

    return {
      campaign_name: campaign.name,
      description: campaign.description,
      goal: '',
      enrollment_type: enrollmentType as 'manual' | 'auto_trigger',
      auto_enrollment_config: campaign.auto_enrollment_config,
      auto_enrollment_trigger: autoEnrollmentTrigger,
      send_time_preference: 'business_hours' as const,
      business_hours_start: '09:00:00',
      business_hours_end: '17:00:00',
      timezone: 'Asia/Kolkata',
      // Auto-enable re-enrollment for status_change campaigns so each status transition re-triggers the email
      allow_re_enrollment: enrollmentType === 'auto_trigger',
      steps: apiSteps
    };
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Transform and validate
      const apiPayload = transformVisualToApi();

      console.log('Saving campaign with payload:', apiPayload);
      console.log('Auto-enrollment config:', apiPayload.auto_enrollment_config);

      // Save to API
      if (campaign.id) {
        // Update existing campaign - use UUID if available, otherwise use ID
        const campaignIdentifier = initialCampaign?.uuid || campaign.id;
        await updateCampaign(campaignIdentifier, apiPayload as any);
        toast.success('Campaign updated successfully!');
      } else {
        // Create new campaign
        await createCampaign(apiPayload as any);
        toast.success('Campaign created successfully!');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving campaign:', error);
      toast.error(error.message || 'Failed to save campaign');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete selected step
  const handleDeleteStep = () => {
    if (!selectedStep) return;
    setStepToDelete(selectedStep);
    setShowDeleteStepModal(true);
  };

  // Confirm step deletion
  const confirmDeleteStep = async () => {
    if (!stepToDelete) return;

    // Remove the step and update connections
    setCampaign(prev => {
      const newSteps = prev.steps.filter(s => s.id !== stepToDelete.id);

      // Update any steps that pointed to the deleted step
      newSteps.forEach(step => {
        if (step.nextStepId === stepToDelete.id) {
          step.nextStepId = stepToDelete.nextStepId;
        }
        if (step.yesStepId === stepToDelete.id) {
          step.yesStepId = undefined;
        }
        if (step.noStepId === stepToDelete.id) {
          step.noStepId = undefined;
        }
      });

      return { ...prev, steps: newSteps };
    });

    setSelectedStep(null);
    toast.success('Step deleted');
  };

  // Auto-layout steps in a vertical flow
  const handleAutoLayout = () => {
    setCampaign(prev => {
      const layoutSteps = [...prev.steps];
      let currentY = 100;
      const stepHeight = 150;
      const x = 300;

      // Find the first step (no incoming connections)
      const firstStep = layoutSteps.find(step => {
        return !layoutSteps.some(s =>
          s.nextStepId === step.id ||
          s.yesStepId === step.id ||
          s.noStepId === step.id
        );
      });

      if (!firstStep) {
        // No clear start, just layout in order
        layoutSteps.forEach((step, index) => {
          step.position = { x, y: 100 + (index * stepHeight) };
        });
      } else {
        // Layout following the flow
        const positioned = new Set<string>();
        const queue = [{ step: firstStep, x, y: currentY }];

        while (queue.length > 0) {
          const { step, x: posX, y: posY } = queue.shift()!;

          if (positioned.has(step.id)) continue;

          step.position = { x: posX, y: posY };
          positioned.add(step.id);

          // Add connected steps to queue
          if (step.nextStepId) {
            const nextStep = layoutSteps.find(s => s.id === step.nextStepId);
            if (nextStep && !positioned.has(nextStep.id)) {
              queue.push({ step: nextStep, x: posX, y: posY + stepHeight });
            }
          }

          if (step.yesStepId) {
            const yesStep = layoutSteps.find(s => s.id === step.yesStepId);
            if (yesStep && !positioned.has(yesStep.id)) {
              queue.push({ step: yesStep, x: posX + 300, y: posY + stepHeight });
            }
          }

          if (step.noStepId) {
            const noStep = layoutSteps.find(s => s.id === step.noStepId);
            if (noStep && !positioned.has(noStep.id)) {
              queue.push({ step: noStep, x: posX - 300, y: posY + stepHeight });
            }
          }
        }
      }

      return { ...prev, steps: layoutSteps };
    });

    toast.success('Layout applied');
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);

    // Determine if this is a new step from palette or existing step
    const stepType = active.data.current?.type;
    if (stepType && !campaign.steps.find(s => s.id === active.id)) {
      setDragType('new');
    } else {
      setDragType('existing');
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over, delta } = event;

    if (!over || over.id !== 'canvas-drop-zone') {
      setActiveId(null);
      setDragType(null);
      return;
    }

    if (dragType === 'new') {
      // Add new step to canvas
      const stepType = active.data.current?.type as 'email' | 'delay' | 'conditional';
      const canvasRect = canvasRef.current?.getBoundingClientRect();

      if (canvasRect) {
        // Get the mouse position at drop time
        const dropX = (event.activatorEvent as MouseEvent).clientX + delta.x;
        const dropY = (event.activatorEvent as MouseEvent).clientY + delta.y;

        // Calculate position relative to canvas, accounting for scroll
        const canvas = canvasRef.current;
        const scrollLeft = canvas?.scrollLeft || 0;
        const scrollTop = canvas?.scrollTop || 0;

        const newStep: CampaignStep = {
          id: `step-${Date.now()}`,
          type: stepType,
          order: campaign.steps.length + 1,
          position: {
            x: Math.max(0, dropX - canvasRect.left + scrollLeft - 128), // Center the step (256px width / 2)
            y: Math.max(0, dropY - canvasRect.top + scrollTop - 50)
          },
          data: stepType === 'delay' ? { delayValue: 1, delayUnit: 'days' } : {}
        };

        setCampaign(prev => ({
          ...prev,
          steps: [...prev.steps, newStep]
        }));

        toast.success(`${stepType.charAt(0).toUpperCase() + stepType.slice(1)} step added`);
      }
    } else if (dragType === 'existing') {
      // Move existing step
      setCampaign(prev => ({
        ...prev,
        steps: prev.steps.map(step =>
          step.id === active.id
            ? {
                ...step,
                position: {
                  x: Math.max(0, step.position.x + delta.x),
                  y: Math.max(0, step.position.y + delta.y)
                }
              }
            : step
        )
      }));
    }

    setActiveId(null);
    setDragType(null);
  };

  const toggleCampaignStatus = async () => {
    if (!campaign.id) return;
    const campaignIdentifier = initialCampaign?.uuid || campaign.id;
    try {
      if (campaign.status === 'active') {
        await pauseCampaign(campaignIdentifier as any);
        setCampaign(prev => ({ ...prev, status: 'paused' }));
        toast.success('Campaign paused');
      } else {
        await activateCampaign(campaignIdentifier as any);
        setCampaign(prev => ({ ...prev, status: 'active' }));
        toast.success('Campaign activated');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update campaign status');
    }
  };

  // Quick action: Add email step
  const handleQuickAddEmailStep = () => {
    const newStep: CampaignStep = {
      id: `step-${Date.now()}`,
      type: 'email',
      order: campaign.steps.length + 1,
      position: {
        x: 100,
        y: 100 + (campaign.steps.length * 200)
      },
      data: {
        stepName: `Email ${campaign.steps.filter(s => s.type === 'email').length + 1}`,
        trackOpens: true
      }
    };

    setCampaign(prev => ({
      ...prev,
      steps: [...prev.steps, newStep]
    }));

    setSelectedStep(newStep);
    toast.success('Email step added');
  };

  // Quick action: Add WhatsApp step
  const handleQuickAddWhatsAppStep = () => {
    const newStep: CampaignStep = {
      id: `step-${Date.now()}`,
      type: 'whatsapp',
      order: campaign.steps.length + 1,
      position: {
        x: 100,
        y: 100 + (campaign.steps.length * 200)
      },
      data: {
        stepName: `WhatsApp ${campaign.steps.filter(s => s.type === 'whatsapp').length + 1}`
      }
    };

    setCampaign(prev => ({
      ...prev,
      steps: [...prev.steps, newStep]
    }));

    setSelectedStep(newStep);
    toast.success('WhatsApp step added');
  };

  // Create a new step and link it to a parent step
  const createAndLinkStep = (parentStep: CampaignStep, stepType: 'email' | 'whatsapp' | 'delay' | 'conditional', linkType: 'next' | 'yes' | 'no') => {
    // Position calculation based on parent position and link type
    let xOffset = 0;
    let yOffset = 200;

    if (linkType === 'yes') {
      xOffset = 350; // Right side for YES
    } else if (linkType === 'no') {
      xOffset = -350; // Left side for NO
    }

    const newStep: CampaignStep = {
      id: `step-${Date.now()}`,
      type: stepType,
      order: campaign.steps.length + 1,
      position: {
        x: parentStep.position.x + xOffset,
        y: parentStep.position.y + yOffset
      },
      data: stepType === 'email' ? {
        stepName: `Email ${campaign.steps.filter(s => s.type === 'email').length + 1}`,
        trackOpens: true
      } : stepType === 'whatsapp' ? {
        stepName: `WhatsApp ${campaign.steps.filter(s => s.type === 'whatsapp').length + 1}`,
        parameterMapping: {}
      } : stepType === 'delay' ? {
        delayValue: 1,
        delayUnit: 'days'
      } : {
        conditionType: 'email_opened',
        waitHours: 24
      }
    };

    // Update parent step to link to new step
    const updatedParentStep = { ...parentStep };
    if (linkType === 'next') {
      updatedParentStep.nextStepId = newStep.id;
    } else if (linkType === 'yes') {
      updatedParentStep.yesStepId = newStep.id;
    } else if (linkType === 'no') {
      updatedParentStep.noStepId = newStep.id;
    }

    // Update campaign with new step and updated parent
    setCampaign(prev => ({
      ...prev,
      steps: [...prev.steps.map(s => s.id === parentStep.id ? updatedParentStep : s), newStep]
    }));

    // Select the newly created step
    setSelectedStep(newStep);
    toast.success(`${stepType.charAt(0).toUpperCase() + stepType.slice(1)} step created and linked`);

    return newStep.id;
  };

  // Handle step update with support for creating new steps
  const handleUpdateStepWithCreate = (updatedStep: CampaignStep) => {
    // Check if we need to create a new step for nextStepId
    if (updatedStep.nextStepId && updatedStep.nextStepId.startsWith('__add_')) {
      const stepType = updatedStep.nextStepId.replace('__add_', '').replace('__', '') as 'email' | 'whatsapp' | 'delay' | 'conditional';
      createAndLinkStep(updatedStep, stepType, 'next');
      return;
    }

    // Check if we need to create a new step for yesStepId
    if (updatedStep.yesStepId && updatedStep.yesStepId.startsWith('__add_')) {
      const stepType = updatedStep.yesStepId.replace('__add_', '').replace('__', '') as 'email' | 'whatsapp' | 'delay' | 'conditional';
      createAndLinkStep(updatedStep, stepType, 'yes');
      return;
    }

    // Check if we need to create a new step for noStepId
    if (updatedStep.noStepId && updatedStep.noStepId.startsWith('__add_')) {
      const stepType = updatedStep.noStepId.replace('__add_', '').replace('__', '') as 'email' | 'whatsapp' | 'delay' | 'conditional';
      createAndLinkStep(updatedStep, stepType, 'no');
      return;
    }

    // Normal update - no new step creation
    setCampaign(prev => ({
      ...prev,
      steps: prev.steps.map(s => s.id === updatedStep.id ? updatedStep : s)
    }));

    // CRITICAL: Update selectedStep to keep it in sync
    setSelectedStep(updatedStep);
  };

  return (
    <>
    <FullScreenModal isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-4 flex-1">
          <input
            type="text"
            value={campaign.name}
            onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
            className="text-xl font-semibold text-gray-900 dark:text-white bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors px-2 py-1"
            placeholder="Campaign Name"
          />
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            campaign.status === 'active'
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : campaign.status === 'paused'
              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {campaign.status.toUpperCase()}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {campaign.steps.length > 0 && (
            <button
              onClick={handleAutoLayout}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              title="Auto-arrange steps"
            >
              <Layers className="w-4 h-4" />
              Auto Layout
            </button>
          )}

          {campaign.id && (
            <button
              onClick={toggleCampaignStatus}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                campaign.status === 'active'
                  ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-200'
                  : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200'
              }`}
            >
              {campaign.status === 'active' ? (
                <>
                  <Pause className="w-4 h-4" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Activate
                </>
              )}
            </button>
          )}

          <button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Campaign'}
          </button>

          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 px-6 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('enrollment')}
          className={`px-4 py-3 text-sm font-medium transition-colors relative ${
            activeTab === 'enrollment'
              ? 'text-brandColor dark:text-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          Enrollment Settings
          {activeTab === 'enrollment' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brandColor dark:bg-blue-400" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('workflow')}
          className={`px-4 py-3 text-sm font-medium transition-colors relative ${
            activeTab === 'workflow'
              ? 'text-brandColor dark:text-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          Workflow Builder
          {activeTab === 'workflow' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brandColor dark:bg-blue-400" />
          )}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'workflow' ? (
        /* Main Content - Two Panel Layout */
        <div className="flex flex-1 overflow-hidden">
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {/* Center Canvas - Visual Flow (Expanded) */}
            <Canvas
              ref={canvasRef}
              steps={campaign.steps}
              selectedStep={selectedStep}
              onSelectStep={setSelectedStep}
              templates={templates}
            />

            {/* Right Panel - Properties Editor */}
            <RightPanel
              selectedStep={selectedStep}
              allSteps={campaign.steps}
              templates={templates}
              whatsappTemplates={whatsappTemplates}
              onUpdateStep={handleUpdateStepWithCreate}
              onDeleteStep={handleDeleteStep}
              onClose={() => setSelectedStep(null)}
              onQuickAddEmail={handleQuickAddEmailStep}
              onQuickAddWhatsApp={handleQuickAddWhatsAppStep}
              hasWhatsappIntegration={hasWhatsappIntegration}
              hasEmailTemplates={hasEmailTemplates}
              whatsappProvider={whatsappProvider}
            />

            <DragOverlay>
              {activeId ? <StepDragPreview id={activeId} /> : null}
            </DragOverlay>
          </DndContext>
        </div>
      ) : (
        /* Enrollment Settings Tab */
        <div className="flex-1 overflow-auto bg-white dark:bg-gray-900">
          <div className="max-w-4xl mx-auto p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Auto-Enrollment Configuration
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configure when and how leads should be automatically enrolled in this campaign.
              </p>
            </div>

            <EnrollmentSettings
              config={campaign.auto_enrollment_config || null}
              onChange={(config) => setCampaign(prev => ({ ...prev, auto_enrollment_config: config }))}
            />
          </div>
        </div>
      )}
    </FullScreenModal>

    {/* Delete Step Confirmation Modal */}
    {showDeleteStepModal && stepToDelete && (
      <DeleteConfirmationModal
        isOpen={showDeleteStepModal}
        onClose={() => {
          setShowDeleteStepModal(false);
          setStepToDelete(null);
        }}
        entityId={stepToDelete.id}
        entityType={`step ${stepToDelete.order} (${stepToDelete.type})`}
        deleteFunction={confirmDeleteStep}
      />
    )}
    </>
  );
};

// Left Sidebar removed - functionality moved to Right Panel Quick Actions

// Step Connections Component - Draws SVG lines between steps
interface StepConnectionsProps {
  steps: CampaignStep[];
}

const StepConnections: React.FC<StepConnectionsProps> = ({ steps }) => {
  const getStepCenter = (step: CampaignStep) => ({
    x: step.position.x + 128, // Half of step width (256px / 2)
    y: step.position.y + 50   // Approximate vertical center
  });

  const renderConnection = (
    from: CampaignStep,
    toId: string,
    color: string = '#3B82F6',
    label?: string
  ) => {
    const toStep = steps.find(s => s.id === toId);
    if (!toStep) return null;

    const start = getStepCenter(from);
    const end = getStepCenter(toStep);

    // Calculate control points for smooth curve
    const midY = (start.y + end.y) / 2;
    const path = `M ${start.x} ${start.y} C ${start.x} ${midY}, ${end.x} ${midY}, ${end.x} ${end.y}`;

    return (
      <g key={`${from.id}-${toId}`}>
        {/* Connection line */}
        <path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
          className="transition-all"
        />

        {/* Label for conditional branches */}
        {label && (
          <text
            x={(start.x + end.x) / 2}
            y={(start.y + end.y) / 2 - 10}
            fill={color}
            fontSize="12"
            fontWeight="600"
            textAnchor="middle"
            className="pointer-events-none"
          >
            {label}
          </text>
        )}
      </g>
    );
  };

  // Calculate SVG bounds
  const bounds = steps.reduce(
    (acc, step) => ({
      minX: Math.min(acc.minX, step.position.x),
      minY: Math.min(acc.minY, step.position.y),
      maxX: Math.max(acc.maxX, step.position.x + 256),
      maxY: Math.max(acc.maxY, step.position.y + 100)
    }),
    { minX: 0, minY: 0, maxX: 1000, maxY: 1000 }
  );

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      style={{
        width: bounds.maxX + 200,
        height: bounds.maxY + 200,
        minWidth: '100%',
        minHeight: '100%'
      }}
    >
      {/* Arrow marker definition */}
      <defs>
        <marker
          id="arrowhead"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
        >
          <polygon
            points="0 0, 10 3, 0 6"
            fill="#3B82F6"
          />
        </marker>
      </defs>

      {/* Render all connections */}
      {steps.map(step => {
        const connections = [];

        // Regular next step connection
        if (step.nextStepId) {
          connections.push(renderConnection(step, step.nextStepId, '#3B82F6'));
        }

        // Conditional branch connections
        if (step.yesStepId) {
          connections.push(renderConnection(step, step.yesStepId, '#10B981', 'YES'));
        }
        if (step.noStepId) {
          connections.push(renderConnection(step, step.noStepId, '#EF4444', 'NO'));
        }

        return connections;
      })}
    </svg>
  );
};

// Canvas Component - Visual Flow Area
interface CanvasProps {
  steps: CampaignStep[];
  selectedStep: CampaignStep | null;
  onSelectStep: (step: CampaignStep) => void;
  templates: DripEmailTemplate[];
}

const Canvas = React.forwardRef<HTMLDivElement, CanvasProps>(
  ({ steps, selectedStep, onSelectStep, templates }, ref) => {
    const { setNodeRef, isOver } = useDroppable({
      id: 'canvas-drop-zone'
    });

    return (
      <div
        ref={(node) => {
          setNodeRef(node);
          if (ref) {
            if (typeof ref === 'function') {
              ref(node);
            } else {
              (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
          }
        }}
        className={`flex-1 bg-gray-100 dark:bg-gray-900 overflow-auto p-8 transition-colors ${
          isOver ? 'bg-blue-50 dark:bg-blue-900/20' : ''
        }`}
      >
        <div className="min-h-full">
          {steps.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <GitBranch className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Start Building Your Campaign
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                  Drag and drop steps from the left panel to create your campaign flow.
                  Connect steps to define the sequence.
                </p>
              </div>
            </div>
          ) : (
            <div className="relative">
              {/* SVG Layer for Connections */}
              <StepConnections steps={steps} />

              {/* Step Nodes */}
              {steps.map((step) => (
                <DraggableStepNode
                  key={step.id}
                  step={step}
                  isSelected={selectedStep?.id === step.id}
                  onClick={() => onSelectStep(step)}
                  templates={templates}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
);

Canvas.displayName = 'Canvas';

// Draggable Step Node Component
interface StepNodeProps {
  step: CampaignStep;
  isSelected: boolean;
  onClick: () => void;
  templates: DripEmailTemplate[];
}

const DraggableStepNode: React.FC<StepNodeProps> = ({ step, isSelected, onClick, templates }) => {
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: step.id,
    data: { step }
  });

  const getStepIcon = (type: string) => {
    switch (type) {
      case 'email':
        return Mail;
      case 'whatsapp':
        return MessageCircle;
      case 'delay':
        return Clock;
      case 'conditional':
        return GitBranch;
      default:
        return Mail;
    }
  };

  const getStepColor = (type: string) => {
    switch (type) {
      case 'email':
        return 'blue';
      case 'whatsapp':
        return 'green';
      case 'delay':
        return 'purple';
      case 'conditional':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const Icon = getStepIcon(step.type);
  const color = getStepColor(step.type);

  const style = {
    position: 'absolute' as const,
    left: step.position.x,
    top: step.position.y,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onClick}
      style={style}
      className={`w-64 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md cursor-move transition-all ${
        isSelected
          ? `ring-2 ring-${color}-500 shadow-lg`
          : 'hover:shadow-lg'
      } ${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 bg-${color}-100 dark:bg-${color}-900 rounded-lg`}>
          <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {step.data?.stepName || (
              step.type === 'email' ? 'Email Step' :
              step.type === 'whatsapp' ? 'WhatsApp Step' :
              step.type === 'delay' ? 'Delay' :
              'Conditional Branch'
            )}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {step.data?.templateId
              ? (templates.find(t => t.id === step.data.templateId)?.template_name || `Step ${step.order}`)
              : `Step ${step.order} — no template`}
          </p>
        </div>
      </div>
    </div>
  );
};

// Right Panel Component - Properties Editor
interface RightPanelProps {
  selectedStep: CampaignStep | null;
  onUpdateStep: (step: CampaignStep) => void;
  onDeleteStep: () => void;
  onClose: () => void;
  allSteps: CampaignStep[];
  templates: DripEmailTemplate[];
  whatsappTemplates: IntegrationTemplate[];
  onQuickAddEmail: () => void;
  onQuickAddWhatsApp: () => void;
  hasWhatsappIntegration: boolean | null;
  hasEmailTemplates: boolean | null;
  whatsappProvider?: 'wati' | 'aisensy' | null;
}

const RightPanel: React.FC<RightPanelProps> = ({ selectedStep, onUpdateStep, onDeleteStep, onClose, allSteps, templates, whatsappTemplates, onQuickAddEmail, onQuickAddWhatsApp, hasWhatsappIntegration, hasEmailTemplates, whatsappProvider }) => {
  if (!selectedStep) {
    return (
      <div className="w-80 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-y-auto">
        <div className="h-full p-8">
          <div className="flex flex-col items-center justify-center text-center mb-8">
            <div className="w-12 h-12 mx-auto mb-3 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Select a step to edit its properties
            </p>
          </div>

          {/* Quick Actions */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Quick Actions
            </h3>
            <div className="space-y-2">
              <button
                onClick={onQuickAddEmail}
                disabled={hasEmailTemplates === false}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                  hasEmailTemplates === false
                    ? 'text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
                    : 'text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                }`}
              >
                <Plus className="w-4 h-4" />
                Add Email Step
              </button>
              <button
                onClick={onQuickAddWhatsApp}
                disabled={hasWhatsappIntegration === false}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                  hasWhatsappIntegration === false
                    ? 'text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 cursor-not-allowed'
                    : 'text-green-600 dark:text-green-400 border border-green-300 dark:border-green-800 hover:bg-green-50 dark:hover:bg-green-900/20'
                }`}
              >
                <Plus className="w-4 h-4" />
                Add WhatsApp Step
              </button>
            </div>

            {/* Info panels for missing integrations */}
            {hasEmailTemplates === false && (
              <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  <span className="font-medium">No email templates found.</span> You need to create email templates first before adding email steps to your campaign.
                </p>
              </div>
            )}

            {hasWhatsappIntegration === false && (
              <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  <span className="font-medium">WhatsApp not configured.</span> You need to set up a WhatsApp integration (AISensy) in Settings &gt; Integrations before adding WhatsApp steps.
                </p>
              </div>
            )}

            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-xs text-blue-800 dark:text-blue-300">
                <span className="font-medium">Tip:</span> Click on a step in the canvas to edit it, or use Quick Actions to add new steps.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-y-auto">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Step Properties
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={onDeleteStep}
              className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded"
              title="Delete step"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Step Type Badge */}
        <div className="mb-4">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
            {selectedStep.type === 'email' ? <Mail className="w-3 h-3" /> :
             selectedStep.type === 'whatsapp' ? <MessageCircle className="w-3 h-3" /> :
             selectedStep.type === 'delay' ? <Clock className="w-3 h-3" /> :
             <GitBranch className="w-3 h-3" />}
            {selectedStep.type.charAt(0).toUpperCase() + selectedStep.type.slice(1)} Step
          </span>
        </div>

        {/* Properties Form */}
        <div className="space-y-4">
          {selectedStep.type === 'email' && (
            <EmailStepProperties step={selectedStep} onUpdate={onUpdateStep} allSteps={allSteps} templates={templates} />
          )}
          {selectedStep.type === 'whatsapp' && (
            <WhatsAppStepProperties step={selectedStep} onUpdate={onUpdateStep} allSteps={allSteps} templates={whatsappTemplates} whatsappProvider={whatsappProvider} />
          )}
          {selectedStep.type === 'delay' && (
            <DelayStepProperties step={selectedStep} onUpdate={onUpdateStep} allSteps={allSteps} />
          )}
          {selectedStep.type === 'conditional' && (
            <ConditionalStepProperties step={selectedStep} onUpdate={onUpdateStep} allSteps={allSteps} />
          )}
        </div>
      </div>
    </div>
  );
};

// Email Step Properties
interface EmailStepPropertiesProps {
  step: CampaignStep;
  onUpdate: (step: CampaignStep) => void;
  allSteps: CampaignStep[];
  templates: DripEmailTemplate[];
}

const EmailStepProperties: React.FC<EmailStepPropertiesProps> = ({ step, onUpdate, allSteps, templates }) => {
  const availableSteps = allSteps.filter(s => s.id !== step.id);

  const handleFieldChange = (field: string, value: any) => {
    onUpdate({
      ...step,
      data: { ...(step.data || {}), [field]: value }
    });
  };

  const handleNextStepChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      ...step,
      nextStepId: e.target.value || undefined
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Step Name
        </label>
        <input
          type="text"
          value={step.data?.stepName || ''}
          onChange={(e) => handleFieldChange('stepName', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="e.g., Welcome Email"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Email Template *
        </label>
        {templates.length === 0 ? (
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-xs text-amber-800 dark:text-amber-300">
              <span className="font-medium">No email templates available.</span> Create email templates first before adding email steps to your campaign.
            </p>
          </div>
        ) : (
          <>
            <select
              value={step.data?.templateId || ''}
              onChange={(e) => handleFieldChange('templateId', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select template...</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.template_name} ({template.category})
                </option>
              ))}
            </select>
            {step.data?.templateId && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                ✓ Template selected
              </p>
            )}
          </>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Subject Line (Optional)
        </label>
        <input
          type="text"
          value={step.data?.subject || ''}
          onChange={(e) => handleFieldChange('subject', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Override template subject..."
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Leave empty to use template's default subject
        </p>
      </div>

      <div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={step.data?.trackOpens !== false}
            onChange={(e) => handleFieldChange('trackOpens', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Track email opens
        </label>
      </div>

      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Next Step
        </label>
        <select
          value={step.nextStepId || ''}
          onChange={handleNextStepChange}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">End campaign</option>
          {availableSteps.map(s => (
            <option key={s.id} value={s.id}>
              Step {s.order}: {s.type === 'email' ? 'Email' : s.type === 'whatsapp' ? 'WhatsApp' : s.type === 'delay' ? 'Delay' : 'Conditional'}
            </option>
          ))}
          <option disabled>────────</option>
          <option value="__add_email__">➕ Add Email Step</option>
          <option value="__add_whatsapp__">➕ Add WhatsApp Step</option>
          <option value="__add_delay__">➕ Add Delay</option>
          <option value="__add_conditional__">➕ Add Conditional Branch</option>
        </select>
      </div>
    </div>
  );
};

// WhatsApp Step Properties
interface WhatsAppStepPropertiesProps {
  step: CampaignStep;
  onUpdate: (step: CampaignStep) => void;
  allSteps: CampaignStep[];
  templates: IntegrationTemplate[];
  whatsappProvider?: 'wati' | 'aisensy' | null;
}

const WhatsAppStepProperties: React.FC<WhatsAppStepPropertiesProps> = ({ step, onUpdate, allSteps, templates, whatsappProvider }) => {
  const settingsLink = whatsappProvider === 'aisensy' ? '/settings/aisensy' : '/settings/wati';
  const availableSteps = allSteps.filter(s => s.id !== step.id);

  const handleFieldChange = (field: string, value: any) => {
    onUpdate({
      ...step,
      data: { ...(step.data || {}), [field]: value }
    });
  };

  const handleNextStepChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      ...step,
      nextStepId: e.target.value || undefined
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Step Name
        </label>
        <input
          type="text"
          value={step.data?.stepName || ''}
          onChange={(e) => handleFieldChange('stepName', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
          placeholder="e.g., WhatsApp Follow-up"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          WhatsApp Template *
        </label>
        {templates.length === 0 ? (
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-xs text-amber-800 dark:text-amber-300">
              <span className="font-medium">No WhatsApp templates available.</span> Configure WhatsApp integration in Settings &gt; Integrations to add templates.
            </p>
          </div>
        ) : (
          <>
            <select
              value={step.data?.templateId || ''}
              onChange={(e) => handleFieldChange('templateId', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Select template...</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.element_name} ({template.language})
                </option>
              ))}
            </select>
            {step.data?.templateId && (() => {
              const tpl = templates.find(t => t.id === step.data?.templateId);
              if (!tpl) return null;
              const paramCount = Array.isArray(tpl.parameters) ? tpl.parameters.length : 0;
              if (paramCount === 0) {
                return <p className="text-xs text-green-600 dark:text-green-400 mt-1">✓ Template selected (no parameters)</p>;
              }
              const mappedCount = Object.values(tpl.parameter_mapping || {}).filter(v => v).length;
              if (mappedCount === 0) {
                return (
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    ⚠ {paramCount} parameter{paramCount > 1 ? 's' : ''} not mapped — <a href={settingsLink} target="_blank" className="underline">configure in WhatsApp settings</a>
                  </p>
                );
              }
              if (mappedCount < paramCount) {
                return (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    ⚠ {mappedCount}/{paramCount} parameters mapped — <a href={settingsLink} target="_blank" className="underline">configure in WhatsApp settings</a>
                  </p>
                );
              }
              return <p className="text-xs text-green-600 dark:text-green-400 mt-1">✓ Template selected · all {paramCount} parameters mapped</p>;
            })()}
          </>
        )}
      </div>

      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Next Step
        </label>
        <select
          value={step.nextStepId || ''}
          onChange={handleNextStepChange}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="">End campaign</option>
          {availableSteps.map(s => (
            <option key={s.id} value={s.id}>
              Step {s.order}: {s.type === 'email' ? 'Email' : s.type === 'whatsapp' ? 'WhatsApp' : s.type === 'delay' ? 'Delay' : 'Conditional'}
            </option>
          ))}
          <option disabled>────────</option>
          <option value="__add_email__">➕ Add Email Step</option>
          <option value="__add_whatsapp__">➕ Add WhatsApp Step</option>
          <option value="__add_delay__">➕ Add Delay</option>
          <option value="__add_conditional__">➕ Add Conditional Branch</option>
        </select>
      </div>
    </div>
  );
};

// Delay Step Properties
interface DelayStepPropertiesProps {
  step: CampaignStep;
  onUpdate: (step: CampaignStep) => void;
  allSteps: CampaignStep[];
}

const DelayStepProperties: React.FC<DelayStepPropertiesProps> = ({ step, onUpdate, allSteps }) => {
  const availableSteps = allSteps.filter(s => s.id !== step.id);

  const handleFieldChange = (field: string, value: any) => {
    onUpdate({
      ...step,
      data: { ...(step.data || { delayValue: 1, delayUnit: 'days' }), [field]: value }
    });
  };

  const handleNextStepChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      ...step,
      nextStepId: e.target.value || undefined
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Delay Duration
        </label>
        <div className="flex gap-2">
          <input
            type="number"
            value={step.data?.delayValue || 1}
            onChange={(e) => handleFieldChange('delayValue', Math.max(1, parseInt(e.target.value) || 1))}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="1"
            min="1"
          />
          <select
            value={step.data?.delayUnit || 'days'}
            onChange={(e) => handleFieldChange('delayUnit', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="minutes">Minutes</option>
            <option value="hours">Hours</option>
            <option value="days">Days</option>
            <option value="weeks">Weeks</option>
          </select>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Wait {step.data?.delayValue || 1} {step.data?.delayUnit || 'days'} before next step
        </p>
      </div>

      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p className="text-xs text-blue-800 dark:text-blue-200">
          💡 The next step will execute after this delay period.
        </p>
      </div>

      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Next Step
        </label>
        <select
          value={step.nextStepId || ''}
          onChange={handleNextStepChange}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">End campaign</option>
          {availableSteps.map(s => (
            <option key={s.id} value={s.id}>
              Step {s.order}: {s.type === 'email' ? 'Email' : s.type === 'whatsapp' ? 'WhatsApp' : s.type === 'delay' ? 'Delay' : 'Conditional'}
            </option>
          ))}
          <option disabled>────────</option>
          <option value="__add_email__">➕ Add Email Step</option>
          <option value="__add_whatsapp__">➕ Add WhatsApp Step</option>
          <option value="__add_delay__">➕ Add Delay</option>
          <option value="__add_conditional__">➕ Add Conditional Branch</option>
        </select>
      </div>
    </div>
  );
};

// Conditional Step Properties
interface ConditionalStepPropertiesProps {
  step: CampaignStep;
  onUpdate: (step: CampaignStep) => void;
  allSteps: CampaignStep[];
}

const ConditionalStepProperties: React.FC<ConditionalStepPropertiesProps> = ({ step, onUpdate, allSteps }) => {
  const availableSteps = allSteps.filter(s => s.id !== step.id);

  const handleYesStepChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      ...step,
      yesStepId: e.target.value || undefined
    });
  };

  const handleNoStepChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      ...step,
      noStepId: e.target.value || undefined
    });
  };

  const handleFieldChange = (field: string, value: any) => {
    onUpdate({
      ...step,
      data: { ...(step.data || { conditionType: 'email_opened', waitHours: 24 }), [field]: value }
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Condition Type
        </label>
        <select
          value={step.data?.conditionType || 'email_opened'}
          onChange={(e) => handleFieldChange('conditionType', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="email_opened">Email Opened</option>
          <option value="email_clicked">Email Clicked</option>
          <option value="time_based">Time-based</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Wait Time (hours)
        </label>
        <input
          type="number"
          value={step.data?.waitHours || 24}
          onChange={(e) => handleFieldChange('waitHours', Math.max(1, parseInt(e.target.value) || 24))}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          min="1"
          placeholder="24"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Wait {step.data?.waitHours || 24} hours before evaluating condition
        </p>
      </div>

      <div className="space-y-3">
        <div className="p-3 border-2 border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">YES Path</span>
          </div>
          <select
            value={step.yesStepId || ''}
            onChange={handleYesStepChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">End campaign</option>
            {availableSteps.map(s => (
              <option key={s.id} value={s.id}>
                Step {s.order}: {s.type === 'email' ? 'Email' : s.type === 'whatsapp' ? 'WhatsApp' : s.type === 'delay' ? 'Delay' : 'Conditional'}
              </option>
            ))}
            <option disabled>────────</option>
            <option value="__add_email__">➕ Add Email Step</option>
            <option value="__add_whatsapp__">➕ Add WhatsApp Step</option>
            <option value="__add_delay__">➕ Add Delay</option>
            <option value="__add_conditional__">➕ Add Conditional Branch</option>
          </select>
          {step.yesStepId && !step.yesStepId.startsWith('__') && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              ✓ YES path configured
            </p>
          )}
        </div>

        <div className="p-3 border-2 border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">NO Path</span>
          </div>
          <select
            value={step.noStepId || ''}
            onChange={handleNoStepChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="">End campaign</option>
            {availableSteps.map(s => (
              <option key={s.id} value={s.id}>
                Step {s.order}: {s.type === 'email' ? 'Email' : s.type === 'whatsapp' ? 'WhatsApp' : s.type === 'delay' ? 'Delay' : 'Conditional'}
              </option>
            ))}
            <option disabled>────────</option>
            <option value="__add_email__">➕ Add Email Step</option>
            <option value="__add_whatsapp__">➕ Add WhatsApp Step</option>
            <option value="__add_delay__">➕ Add Delay</option>
            <option value="__add_conditional__">➕ Add Conditional Branch</option>
          </select>
          {step.noStepId && !step.noStepId.startsWith('__') && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              ✓ NO path configured
            </p>
          )}
        </div>
      </div>

      <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
        <p className="text-xs text-orange-800 dark:text-orange-200">
          The flow will branch based on whether the condition is met within the previous step.
        </p>
      </div>
    </div>
  );
};

// Drag Preview Component
const StepDragPreview: React.FC<{ id: string }> = ({ id }) => {
  // Determine step type from id
  const getStepData = () => {
    if (id.startsWith('palette-')) {
      const type = id.replace('palette-', '');
      return {
        type,
        icon: type === 'email' ? Mail : type === 'delay' ? Clock : GitBranch,
        label: type === 'email' ? 'Email Step' : type === 'delay' ? 'Delay' : 'Conditional Branch',
        color: type === 'email' ? 'blue' : type === 'delay' ? 'purple' : 'orange'
      };
    }
    // For existing steps, show generic preview
    return {
      type: 'step',
      icon: Mail,
      label: 'Campaign Step',
      color: 'blue'
    };
  };

  const { icon: Icon, label, color } = getStepData();

  return (
    <div className="w-64 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg opacity-90">
      <div className="flex items-center gap-3">
        <div className={`p-2 bg-${color}-100 dark:bg-${color}-900 rounded-lg`}>
          <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {label}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CampaignBuilderModal;
